import React, { useState, useEffect, useCallback, useRef } from 'react';
import { PackageSearch, Play, Check, X, RefreshCw, AlertTriangle, Clock, Power } from 'lucide-react';
import { discoveryAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';

const GREEN = '#1B5C3A';
const GOLD = '#F5A623';
const PURPLE = '#4C1D95';
const RED = '#dc2626';
const CREAM = '#FAF9F6';
const CHAR = '#1a1a1a';
const MUTED = '#6b7280';
const BORDER = '#e5e7eb';

const INPUT = { padding: '7px 10px', borderRadius: 8, border: `1px solid ${BORDER}`, fontSize: 13.5, fontFamily: 'var(--font-sans)', width: '100%', boxSizing: 'border-box' };
const fmtDt = (d) => d ? new Date(d).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—';

export default function ProductDiscovery() {
  useAuth();
  const [data, setData] = useState(null);          // { suggestions, runs, status }
  const [tab, setTab] = useState('pending');
  const [sel, setSel] = useState({});              // id -> true (bulk selection)
  const [edits, setEdits] = useState({});          // id -> { name, description, mrp }
  const [busy, setBusy] = useState('');            // id being acted on / 'run' / 'bulk'
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const pollRef = useRef(null);

  const load = useCallback((status = tab) => discoveryAPI.list({ status })
    .then(r => setData(r.data.data || r.data))
    .catch(e => setErr(e.response?.data?.message || 'Failed to load discovery data')), [tab]);
  useEffect(() => { load(tab); setSel({}); }, [tab, load]);

  // poll while a run is in progress so results appear as they land
  useEffect(() => {
    if (data?.status?.running && !pollRef.current) {
      pollRef.current = setInterval(() => load(), 6000);
    } else if (!data?.status?.running && pollRef.current) {
      clearInterval(pollRef.current); pollRef.current = null;
    }
    return () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  }, [data?.status?.running, load]);

  const st = data?.status || {};
  const suggestions = data?.suggestions || [];
  const runs = data?.runs || [];
  const lastRun = runs[0];
  const selIds = Object.keys(sel).filter(id => sel[id]);

  const runNow = () => {
    setBusy('run'); setMsg(''); setErr('');
    discoveryAPI.run()
      .then(r => { setMsg(r.data.message || 'Run started.'); setTimeout(() => load(), 2500); })
      .catch(e => setErr(e.response?.data?.message || 'Could not start run'))
      .finally(() => setBusy(''));
  };
  const toggleEnabled = () => {
    discoveryAPI.settings({ enabled: !st.enabled }).then(() => load()).catch(() => setErr('Could not update setting'));
  };
  const approve = (s) => {
    const e = edits[s._id] || {};
    setBusy(s._id); setErr('');
    discoveryAPI.approve(s._id, { name: e.name, description: e.description, mrp: e.mrp })
      .then(() => { setMsg(`Approved — "${(e.name || s.name)}" is now LIVE in the catalog.`); load(); })
      .catch(x => setErr(x.response?.data?.message || 'Approve failed'))
      .finally(() => setBusy(''));
  };
  const reject = (s) => {
    setBusy(s._id); setErr('');
    discoveryAPI.reject(s._id, '')
      .then(() => { setMsg(`Rejected "${s.name}" — it will not be suggested again.`); load(); })
      .catch(x => setErr(x.response?.data?.message || 'Reject failed'))
      .finally(() => setBusy(''));
  };
  const bulk = (action) => {
    if (!selIds.length) return;
    setBusy('bulk'); setErr('');
    discoveryAPI.bulk({ ids: selIds, action })
      .then(r => { const d = r.data.data || {}; setMsg(`Bulk ${action}: ${d.approved || d.rejected || 0} done${d.skipped ? `, ${d.skipped} skipped` : ''}.`); setSel({}); load(); })
      .catch(x => setErr(x.response?.data?.message || 'Bulk action failed'))
      .finally(() => setBusy(''));
  };
  const setEdit = (id, patch) => setEdits(e => ({ ...e, [id]: { ...(e[id] || {}), ...patch } }));

  if (!data && err) return <div style={{ padding: 24 }}><div className="card" style={{ padding: 24, borderLeft: `4px solid ${RED}`, color: RED }}>{err}</div></div>;
  if (!data) return <div style={{ display: 'flex', justifyContent: 'center', minHeight: '60vh', alignItems: 'center', color: MUTED }}>Loading product discovery…</div>;

  return (
    <div style={{ padding: '32px 40px', background: CREAM, minHeight: '100vh' }}>
      <div style={{ marginBottom: 20 }}>
        <h1 style={{ fontFamily: 'var(--font-serif)', fontSize: 32, fontWeight: 800, color: CHAR, margin: 0, letterSpacing: '-0.02em' }}>
          <PackageSearch size={26} style={{ verticalAlign: -4, marginRight: 8, color: GREEN }} />Product Discovery
        </h1>
        <p style={{ color: MUTED, fontSize: 15, marginTop: 6, fontWeight: 500, maxWidth: 860, lineHeight: 1.55 }}>
          Every night the AI scans the market for products missing from your catalog (within your existing categories, sub-categories and brands)
          and stages them here. <b style={{ color: CHAR }}>Nothing goes live until you approve it</b> — approving creates the real catalog item.
        </p>
      </div>

      {/* status card */}
      <div className="card" style={{ padding: 20, marginBottom: 20, display: 'flex', gap: 18, alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13.5, fontWeight: 600, color: CHAR }}>
          <Clock size={16} style={{ color: PURPLE }} />{st.schedule || 'Daily at 10:00 PM IST'}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 600, color: st.keyConfigured ? GREEN : RED }}>
          {st.keyConfigured ? <Check size={15} /> : <AlertTriangle size={15} />}
          {st.keyConfigured ? `AI: ${st.provider} · ${st.model}` : 'AI key not configured'}
        </div>
        {lastRun && (
          <div style={{ fontSize: 12.5, color: MUTED, fontWeight: 500 }}>
            Last run {fmtDt(lastRun.startedAt)} ({lastRun.trigger}) — {lastRun.status}: {lastRun.suggestionsFound} found, {lastRun.combosScanned} combos{lastRun.errorLog?.length ? `, ${lastRun.errorLog.length} errors` : ''}
          </div>
        )}
        <div style={{ flex: 1 }} />
        <button onClick={toggleEnabled} title="Enable/disable the nightly schedule (manual runs always work)" style={{
          display: 'flex', alignItems: 'center', gap: 7, padding: '8px 14px', borderRadius: 9, cursor: 'pointer', fontSize: 13, fontWeight: 700,
          border: `1.5px solid ${st.enabled ? GREEN : BORDER}`, background: st.enabled ? 'rgba(27,92,58,0.08)' : '#fff', color: st.enabled ? GREEN : MUTED,
        }}><Power size={14} />{st.enabled ? 'Nightly: ON' : 'Nightly: OFF'}</button>
        <button onClick={runNow} disabled={busy === 'run' || st.running || !st.keyConfigured} style={{
          display: 'flex', alignItems: 'center', gap: 8, padding: '9px 18px', borderRadius: 9, border: 'none', cursor: 'pointer',
          background: st.running ? GOLD : GREEN, color: '#fff', fontSize: 13.5, fontWeight: 700, opacity: (!st.keyConfigured || busy === 'run') ? 0.55 : 1,
        }}>
          {st.running ? <RefreshCw size={15} className="spin" /> : <Play size={15} />}
          {st.running ? 'Scanning market…' : 'Run now'}
        </button>
      </div>
      <style>{`.spin { animation: spin 1.2s linear infinite; } @keyframes spin { to { transform: rotate(360deg); } }`}</style>

      {!st.keyConfigured && (
        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '14px 16px', marginBottom: 20, borderRadius: 10, background: 'rgba(220,38,38,0.06)', border: `1px solid ${RED}44`, fontSize: 13.5, color: CHAR, fontWeight: 500, lineHeight: 1.55 }}>
          <AlertTriangle size={18} style={{ color: RED, flexShrink: 0, marginTop: 1 }} />
          <span>
            <b>One-time setup:</b> add an AI key to the backend's <code>.env</code> on the server — <code>ANTHROPIC_API_KEY</code> (recommended, uses Claude Haiku — very low cost)
            or <code>OPENAI_API_KEY</code> / <code>GEMINI_API_KEY</code> — then restart the backend (<code>pm2 restart kanjusee-backend</code>). The nightly scan starts working immediately after.
          </span>
        </div>
      )}

      {msg && <div style={{ padding: '10px 16px', marginBottom: 14, borderRadius: 10, background: 'rgba(27,92,58,0.08)', color: GREEN, fontSize: 13.5, fontWeight: 600 }}>{msg}</div>}
      {err && <div style={{ padding: '10px 16px', marginBottom: 14, borderRadius: 10, background: 'rgba(220,38,38,0.07)', color: RED, fontSize: 13.5, fontWeight: 600 }}>{err}</div>}

      {/* tabs */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, alignItems: 'center', flexWrap: 'wrap' }}>
        {[['pending', `Pending (${st.counts?.pending ?? 0})`], ['approved', `Approved (${st.counts?.approved ?? 0})`], ['rejected', `Rejected (${st.counts?.rejected ?? 0})`]].map(([k, t]) => (
          <button key={k} onClick={() => setTab(k)} style={{
            padding: '8px 16px', borderRadius: 18, cursor: 'pointer', fontSize: 13, fontWeight: 700, fontFamily: 'var(--font-sans)',
            border: `1.5px solid ${tab === k ? GREEN : BORDER}`, background: tab === k ? GREEN : '#fff', color: tab === k ? '#fff' : MUTED,
          }}>{t}</button>
        ))}
        <div style={{ flex: 1 }} />
        {tab === 'pending' && selIds.length > 0 && (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <span style={{ fontSize: 12.5, color: MUTED, fontWeight: 600 }}>{selIds.length} selected</span>
            <button onClick={() => bulk('approve')} disabled={busy === 'bulk'} style={{ padding: '7px 14px', borderRadius: 8, border: 'none', cursor: 'pointer', background: GREEN, color: '#fff', fontSize: 12.5, fontWeight: 700 }}>Approve selected</button>
            <button onClick={() => bulk('reject')} disabled={busy === 'bulk'} style={{ padding: '7px 14px', borderRadius: 8, border: `1.5px solid ${RED}`, cursor: 'pointer', background: '#fff', color: RED, fontSize: 12.5, fontWeight: 700 }}>Reject selected</button>
          </div>
        )}
      </div>

      {/* suggestions */}
      {suggestions.length === 0 ? (
        <div className="card" style={{ padding: '48px 24px', textAlign: 'center', color: MUTED }}>
          <PackageSearch size={36} style={{ opacity: 0.3 }} />
          <p style={{ fontSize: 15, fontWeight: 600, margin: '12px 0 4px', color: CHAR }}>No {tab} suggestions</p>
          <p style={{ fontSize: 13.5, fontWeight: 500, margin: 0 }}>
            {tab === 'pending'
              ? (st.keyConfigured ? 'The next nightly scan runs at 10 PM IST — or hit "Run now".' : 'Configure an AI key above to start discovering products.')
              : 'Nothing here yet.'}
          </p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {suggestions.map(s => {
            const e = edits[s._id] || {};
            const pending = s.status === 'pending';
            return (
              <div key={s._id} className="card" style={{ padding: 18, display: 'flex', gap: 14, alignItems: 'flex-start', borderLeft: `4px solid ${s.status === 'approved' ? GREEN : s.status === 'rejected' ? BORDER : GOLD}` }}>
                {pending && (
                  <input type="checkbox" checked={!!sel[s._id]} onChange={ev => setSel(x => ({ ...x, [s._id]: ev.target.checked }))} style={{ marginTop: 6, width: 16, height: 16, cursor: 'pointer' }} />
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                  {pending
                    ? <input style={{ ...INPUT, fontWeight: 700, fontSize: 15, marginBottom: 6 }} value={e.name ?? s.name} onChange={ev => setEdit(s._id, { name: ev.target.value })} />
                    : <div style={{ fontWeight: 700, fontSize: 15.5, color: CHAR, marginBottom: 4 }}>{s.name}</div>}
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8, fontSize: 11.5, fontWeight: 700 }}>
                    <span style={{ padding: '3px 9px', background: 'rgba(76,29,149,0.08)', color: PURPLE, borderRadius: 10 }}>{s.categoryId?.name} › {s.subCategoryId?.name} › {s.brandId?.name}</span>
                    <span style={{ padding: '3px 9px', background: '#f3f4f6', color: MUTED, borderRadius: 10 }}>confidence {(s.confidence * 100).toFixed(0)}%</span>
                    {s.approxMrpInr > 0 && <span style={{ padding: '3px 9px', background: 'rgba(27,92,58,0.08)', color: GREEN, borderRadius: 10 }}>≈ ₹{Number(pending ? (e.mrp ?? s.approxMrpInr) : s.approxMrpInr).toLocaleString('en-IN')}</span>}
                    <span style={{ padding: '3px 9px', background: '#f3f4f6', color: MUTED, borderRadius: 10 }}>{fmtDt(s.createdAt)}</span>
                  </div>
                  {pending
                    ? <textarea style={{ ...INPUT, minHeight: 54, resize: 'vertical', marginBottom: 6 }} value={e.description ?? s.description} onChange={ev => setEdit(s._id, { description: ev.target.value })} />
                    : s.description && <p style={{ fontSize: 13.5, color: CHAR, margin: '0 0 6px', lineHeight: 1.5 }}>{s.description}</p>}
                  {s.specs && Object.keys(s.specs).length > 0 && (
                    <div style={{ fontSize: 12, color: MUTED, fontWeight: 500, marginBottom: 4 }}>
                      {Object.entries(s.specs).slice(0, 6).map(([k, v]) => `${k}: ${v}`).join('  ·  ')}
                    </div>
                  )}
                  {s.reason && <div style={{ fontSize: 12, color: MUTED, fontStyle: 'italic' }}>AI: {s.reason}</div>}
                  {s.status === 'rejected' && s.rejectionReason && <div style={{ fontSize: 12, color: RED }}>Rejected: {s.rejectionReason}</div>}
                  {s.status !== 'pending' && s.reviewedBy?.fullName && <div style={{ fontSize: 11.5, color: MUTED, marginTop: 4 }}>{s.status} by {s.reviewedBy.fullName} · {fmtDt(s.reviewedAt)}</div>}
                </div>
                {pending && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8, flexShrink: 0 }}>
                    <div>
                      <label style={{ fontSize: 10, fontWeight: 700, color: MUTED, textTransform: 'uppercase' }}>MRP ₹ (internal)</label>
                      <input style={{ ...INPUT, width: 120 }} type="number" min="0" value={e.mrp ?? s.approxMrpInr} onChange={ev => setEdit(s._id, { mrp: ev.target.value })} />
                    </div>
                    <button onClick={() => approve(s)} disabled={busy === s._id} style={{ display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'center', padding: '9px 16px', borderRadius: 8, border: 'none', cursor: 'pointer', background: GREEN, color: '#fff', fontSize: 13, fontWeight: 700, opacity: busy === s._id ? 0.6 : 1 }}>
                      <Check size={15} />Approve → Live
                    </button>
                    <button onClick={() => reject(s)} disabled={busy === s._id} style={{ display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'center', padding: '8px 16px', borderRadius: 8, border: `1.5px solid ${RED}`, cursor: 'pointer', background: '#fff', color: RED, fontSize: 13, fontWeight: 700 }}>
                      <X size={15} />Reject
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <p style={{ fontSize: 11.5, color: MUTED, marginTop: 20, textAlign: 'center' }}>
        Suggestions are AI-generated and may occasionally be wrong — that's why your approval is the gate. Rejected items are remembered and never re-suggested.
      </p>
    </div>
  );
}
